import type { Strings } from "./types";

const mono = (text: string) => <span className="font-mono">{text}</span>;

export const nl: Strings = {
  locale: "nl-NL",
  euro: (amount) => `€${amount}`,
  today: "vandaag",

  app: {
    navAbout: "Over ons",
    navApi: "API",
    openMenu: "Menu openen",
    loadingPrices: "Prijsgegevens laden...",
    loadError: "Fout bij het laden van de prijsgegevens. Probeer het later opnieuw.",
    pricesUnavailableTitle: "Prijsgegevens niet beschikbaar",
    noDataForDate:
      "Geen prijsdata beschikbaar voor deze dag. Kies een andere datum met de pijlen bij de grafiek.",
    tomorrowDelayedTitle: "Prijzen voor morgen zijn later dan gebruikelijk",
    tomorrowDelayedBody: "ENTSO-E heeft de prijzen nog niet gepubliceerd.",
    tomorrowPendingTitle: "Nog geen prijzen voor morgen bekend",
    tomorrowPendingBody: "De prijzen voor morgen worden doorgaans rond 13:00 gepubliceerd.",
    tomorrowNotAvailable: "De prijzen voor morgen zijn nog niet beschikbaar.",
    dateLoadError:
      "Fout bij het laden van de prijsgegevens voor deze dag. Probeer een andere datum.",
    closeNotice: "Melding sluiten",
    closeDialog: "Sluiten",
    nowLabel: "Nu",
    currentTimeLabel: "Huidige tijd",
    chartControls: "Grafiekbediening",
    previousDay: "Vorige dag",
    nextDay: "Volgende dag",
    chartTitle: "All-in energieprijzen",
    chartHeading: (dateLabel) => `All-in energieprijzen ${dateLabel}`,
    explainer:
      "De grafiek toont de energieprijs op de energiemarkt. Energieleveranciers rekenen daarbovenop hun eigen opslag en vaste maandelijkse kosten. Deze verschillen worden in de tabel hieronder weergegeven.",
    batteryCtaQuestion:
      "Ook weten welke aanbieder het beste samenwerkt met een thuisbatterij?",
    batteryCtaLink: (ctaName) => `Kijk op ${ctaName}`,
    footerUpdates:
      "Energieprijzen worden dagelijks bijgewerkt, de prijzen van morgen zijn rond 13:00 uur beschikbaar.",
    footerInitiative: "Een initiatief van",
  },

  correction: {
    open: "Correctie doorgeven",
    title: "Correctie doorgeven",
    emailLabel: "E-mailadres (optioneel)",
    emailPlaceholder: "jij@example.com",
    messageLabel: "Beschrijving",
    messagePlaceholder: "Beschrijf wat er gecorrigeerd moet worden…",
    sendError: "Verzenden mislukt. Probeer het later opnieuw.",
    success: "Bedankt! Je correctie is verstuurd.",
    close: "Sluiten",
    sending: "Versturen…",
    send: "Versturen",
  },

  controls: {
    region: "Regio",
    regionPlaceholder: "Selecteer regio",
    provider: "Leverancier",
    providerPlaceholder: "Selecteer energieleverancier",
    factor: "Factor",
    markup: "Toeslag",
    monthlyFee: "Vastrecht",
    perMonth: "/mnd",
    intervalGroup: "Prijsinterval",
    hour: "Uur",
    quarter: "15 min",
    inclTaxes: "Incl. belastingen",
    exclTaxes: "Excl. belastingen",
    taxesToggle: "Belastingen opnemen",
  },

  summary: {
    sectionLabel: (dateLabel) => `Prijsinzichten voor ${dateLabel}`,
    currentPrice: "Huidige prijs",
    averagePrice: "Gemiddelde prijs",
    perKwh: "per kWh",
    spokenWithCurrent: (current, low, high, average) =>
      `De huidige prijs is ${current} per kWh, tussen ${low} en ${high}. De gemiddelde prijs is ${average}.`,
    spokenAverageOnly: (average, low, high) =>
      `De gemiddelde prijs is ${average} per kWh, tussen ${low} en ${high}.`,
    comparedToToday: "Vergelijking met vandaag",
    averageShort: "Gem.",
    now: "Nu",
    cheapestBlock: "Voordeligste 3-uursblok",
    blockAverage: (price) => `Gemiddeld ${price} per kWh`,
    notEnoughData: "Niet genoeg prijsgegevens voor een aaneengesloten blok.",
    lowest: "Laagste",
    average: "Gemiddeld",
    highest: "Hoogste",
    dayPrice: "dagprijs",
    notAvailable: "Niet beschikbaar",
    noCurrentPrice: "Geen huidige prijs beschikbaar",
    forThisDay: (dateLabel) => `${dateLabel} · voor deze dag`,
    cheapest: (interval) => `Goedkoopste ${interval === "15m" ? "kwartier" : "uur"}`,
    mostExpensive: (interval) => `Duurste ${interval === "15m" ? "kwartier" : "uur"}`,
    averageOn: (dateLabel) => `Gemiddeld ${dateLabel}`,
  },

  chart: {
    energyPrice: "Energieprijs",
    spotFactor: (coefficient) => `Spotfactor leverancier (×${coefficient})`,
    supplierMarkup: "Opslag leverancier",
    vat: "BTW",
    total: "Totaal",
    yAxis: "Prijs (€/kWh)",
    lowestBand: "5% laagste prijzen",
    highestBand: "5% hoogste prijzen",
  },

  providers: {
    title: "Prijzen van alle energieleveranciers",
    noneForRegion: "Nog geen geverifieerde leveranciers voor deze regio.",
    regionNote:
      "We tonen alleen leveranciers waarvan we een tariefkaart voor jouw regio geverifieerd hebben.",
    sortBy: "Sorteren op",
    monthlyEstimate: "Indicatie maandlast",
    name: "Naam",
    monthlyFeeLong: "Vastrecht per maand",
    monthlyFeeShort: "Vastrecht / maand",
    markupLong: "Toeslag per kWh",
    markupShort: "Toeslag / kWh",
    factor: "Factor",
    link: "Link",
    selected: "Geselecteerd",
    visit: "Bezoeken",
    noEstimate: "Geen prijsberekening beschikbaar",
    moreInfoEstimate: "Meer informatie over de indicatieve maandlast",
    inclTaxClause: (energyTaxLabel) => `, inclusief ${energyTaxLabel.toLowerCase()}`,
    estimateBasisExpected: (usageKwh, taxClause) =>
      `Gebaseerd op ${usageKwh} kWh maandverbruik en de verwachte gemiddelde marktprijs voor de komende 12 maanden${taxClause}`,
    estimateBasisDay: (usageKwh, taxClause) =>
      `Gebaseerd op ${usageKwh} kWh maandverbruik en de gemiddelde dagprijs${taxClause}`,
    source: "bron",
    expandDetails: (providerName) => `Details van ${providerName} tonen`,
    collapseDetails: (providerName) => `Details van ${providerName} verbergen`,
    details: "Details",
    moreConditions: (count) => `${count} meer`,
    moreConditionsLabel: (count) => `${count} meer kenmerken tonen`,
    fewerConditions: "Minder",
    fewerConditionsLabel: "Minder kenmerken tonen",
    calculationTitle: "Berekening maandlast",
    perKwh: "per kWh",
    calcMarketInclTaxes: "Marktprijs incl. belastingen",
    calcMarketInclVat: "Marktprijs incl. btw",
    calcMarketExclTaxes: "Marktprijs excl. belastingen",
    calcFactor: (providerName) => `Factor ${providerName}`,
    calcMarkup: (providerName) => `Toeslag ${providerName}`,
    calcUsage: "Verbruik",
    calcEnergyCost: "Stroomkosten",
    calcMonthlyFee: "Vastrecht",
    termInclVat: (vatPercent) => `incl. ${vatPercent}% btw`,
    feedInTitle: "Terugleververgoeding",
    feedInMarketPrice: "marktprijs",
    feedInNoFee: "zonder terugleverkosten",
    feedInVatExempt: "vrijgesteld van btw",
    feedInExample: (marketPrice, revenue) =>
      `Bij een marktprijs van ${marketPrice} ontvang je ${revenue} per kWh.`,
    feedInUnknown: "De terugleververgoeding van deze leverancier is nog niet verzameld.",
    conditionsTitle: "Bonussen en voorwaarden",
    productLabel: "Product",
    settlement15m: "Kwartierafrekening",
    settlement60m: "Uurafrekening",
    settlement15mDetail:
      "Je verbruik wordt per kwartier afgerekend tegen de marktprijs van dat kwartier.",
    settlement60mDetail:
      "Je verbruik wordt per uur afgerekend tegen de marktprijs van dat uur.",
    fixedFormula: "Vaste tariefformule",
    fixedFormulaUntilDetail: (dateLabel) =>
      `Toeslag, vastrecht en factor staan vast tot en met ${dateLabel}.`,
    fixedFormulaYearsDetail: (years) =>
      years === 1
        ? "Toeslag, vastrecht en factor staan een jaar vast vanaf de start van je contract."
        : `Toeslag, vastrecht en factor staan ${years} jaar vast vanaf de start van je contract.`,
    fixedFormulaDetail: "Toeslag, vastrecht en factor staan voor langere tijd vast.",
    noDeposit: "Geen voorschotten",
    noDepositDetail:
      "Je betaalt maandelijks op basis van je werkelijke verbruik, zonder voorschotten.",
    membership: "Lidmaatschap vereist",
    limitedArea: "Beperkt leveringsgebied",
    contractTerm: (years) => (years === 1 ? "Contract 1 jaar" : `Contract ${years} jaar`),
    contractTermDetail: (years) =>
      years === 1
        ? "Contract met een looptijd van een jaar."
        : `Contract met een looptijd van ${years} jaar.`,
    filterTitle: "Filter op kenmerk",
    filters: {
      discount: "Korting 1e jaar",
      quarterHourly: "Kwartierafrekening",
      noDeposit: "Geen voorschotten",
      fixedFormula: "Vaste tariefformule",
      noMembership: "Geen aandeel vereist",
    },
    filterCount: (shown, total) => `${shown} van ${total} leveranciers`,
    clearFilters: "Filters wissen",
    noneMatchFilters: "Geen leveranciers voldoen aan deze filters.",
  },

  about: {
    title: "Over ons",
    intro: (domain) =>
      `${domain} is een project om energieprijzen eenvoudig beschikbaar te maken, online en in smart home systemen. De API wordt voor ons afgehandeld door Cloudflare, waardoor we deze API ook gratis kunnen delen.`,
    sourcePrefix: "De brondata komt van ENTSO-e en wordt verkregen via",
    contact: "Contact:",
  },

  api: {
    title: "API",
    intro: (energyTaxLabel) => (
      <>
        Kies hieronder jouw aanbieder en of je de prijzen per kwartier of per uur
        wilt krijgen. In de response vind je vervolgens 'today' en 'tomorrow'
        (wanneer beschikbaar). De response bevat ook {mono("energy_tax")} met de{" "}
        {energyTaxLabel.toLowerCase()} (inclusief btw) en{" "}
        {mono("provider_charge")} (ook inclusief btw). Hiermee kun je eventueel
        ook de kale energieprijs weer berekenen.
      </>
    ),
    regions: (regionIds, defaultRegionId) => (
      <>
        Elke leverancier heeft één id; met {mono("?region=")} kies je de regio
        (mogelijke waarden: {regionIds}; standaard {defaultRegionId}). Zowel de
        tariefkaart van de leverancier als de gewestelijke heffingen volgen die
        ene keuze, dus ze kunnen niet meer uit elkaar lopen. De gebruikte regio
        staat als {mono("region")} in de response. Vraag je een regio op waarvoor
        we voor die leverancier geen tariefkaart geverifieerd hebben, dan krijg je
        een 404 met de regio's die wel beschikbaar zijn — zie de lijst hieronder.
      </>
    ),
    onThisPage: "Op deze pagina",
    buildUrlLink: "API URL maken",
    providersLink: "Aanbieders",
    buildUrlTitle: "Stel je eigen API URL samen",
    provider: "Aanbieder",
    interval: "Interval",
    quarterly: "per 15 minuten (quarterly)",
    hourly: "per uur (hourly)",
    region: "Regio",
    composedUrl: "Samengestelde API URL",
    copy: "Kopieer",
    copied: "Gekopieerd",
    copyFailed: "Kopiëren mislukt",
    tryIt: "Probeer",
    loading: "Bezig…",
    fetchFailed: "Ophalen mislukt. Probeer het later opnieuw.",
    response: "Response:",
    error: "Fout",
    providersSummary: (count) =>
      `Beschikbare aanbieders en contractvarianten (${count})`,
    missingProvider:
      "Zie je een onjuiste prijsopslag of mist jouw aanbieder? Laat het ons weten.",
    haTitle: "Energieprijzen gebruiken in Home Assistant",
    haIntro:
      "Er zijn verschillende manieren om de energieprijzen in te laden, bijvoorbeeld met de ENTSO-e of Nordpool integraties, aangevuld met de prijsopslag van je aanbieder. Een makkelijkere manier is om de API van deze site te gebruiken om de prijzen op te halen en ze in je systeem op te nemen.",
    haExample:
      "Hieronder vind je een voorbeeld om via de REST integratie twee sensors te maken: een voor vandaag en een voor morgen. Daarna kun je de data tonen in een apexcharts-card.",
    haConfigTitle: "Voeg de volgende configuratie toe aan je configuration.yaml",
    haConfigLabel: "Home Assistant-configuratie",
    apexIntro: (link) => (
      <>
        Vervolgens kun je de volgende configuratie gebruiken op je dashboard,
        nadat je {link} hebt geïnstalleerd.
      </>
    ),
    apexLabel: "ApexCharts-configuratie",
    ha: {
      dataSensor: "EPEX prijzen data",
      dataEntity: "sensor.epex_prijzen_data",
      todaySensor: "EPEX prijzen vandaag",
      tomorrowSensor: "EPEX prijzen morgen",
      lowestTodaySensor: "EPEX laagste prijs vandaag",
      highestTodaySensor: "EPEX hoogste prijs vandaag",
      currentSensor: "EPEX huidige prijs",
      currentQuarterSensor: "EPEX huidige prijs (15m)",
      quarterComment:
        "Gebruik je de kwartierprijzen? Deze sensor geeft dan de huidige prijs weer.",
      chartTitle: "Electriciteitsprijzen (€/kWh)",
      chartNow: "Nu",
      chartToday: "Vandaag",
      chartTomorrow: "Morgen",
    },
    evccTitle: "Energieprijzen gebruiken in evcc.io",
    evccIntro:
      "evcc.io biedt een ingebouwde template voor epexprijzen.nl. Gebruik onderstaande configuratie en kies je aanbieder en interval.",
    evccConfigTitle: "Voeg dit toe aan je evcc-configuratie:",
    evccLabel: "evcc-configuratie",
    evccProviderComment:
      "Energieleverancier zoals hierboven beschreven, bijvoorbeeld anwb-energie",
  },

  errorBoundary: {
    title: "Er ging iets mis.",
    body: "Vernieuw de pagina om het opnieuw te proberen.",
    reload: "Vernieuwen",
  },
};
